
import { supabase, enhancedQuery } from "@/integrations/supabase/client";
import { City } from "@/types/flight";

type CityCreateInput = {
  name: string;
  code: string;
  organization_id?: string;
};

type CityUpdateInput = {
  name?: string;
  code?: string;
  organization_id?: string | null;
};

// Get all cities with optional organization filter
export const getCities = async (organizationId?: string): Promise<City[]> => {
  try {
    let query = supabase.from('cities').select('*');
    
    if (organizationId) {
      // Get both global cities (null organization_id) and organization-specific cities
      query = query.or(`organization_id.eq.${organizationId},organization_id.is.null`);
    }
    
    const { data, error } = await query.order('name');
    
    if (error) {
      console.error('Error fetching cities:', error);
      throw error;
    }
    
    return data as City[];
  } catch (error) {
    console.error('Unexpected error in getCities:', error);
    throw error;
  }
};

// Get a single city by ID
export const getCityById = async (id: string): Promise<City> => {
  return enhancedQuery(async () => {
    const result = await supabase
      .from('cities')
      .select('*')
      .eq('id', id)
      .single();
      
    return result;
  });
};

// Create a new city
export const createCity = async (input: CityCreateInput): Promise<City> => {
  try {
    const { data, error } = await supabase
      .from('cities')
      .insert([
        {
          name: input.name,
          code: input.code,
          organization_id: input.organization_id || null,
        }
      ])
      .select();
    
    if (error) {
      console.error('Error creating city:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      throw new Error('Failed to create city: No data returned');
    }
    
    return data[0] as City;
  } catch (error) {
    console.error('Unexpected error in createCity:', error);
    throw error;
  }
};

// Update an existing city
export const updateCity = async (id: string, input: CityUpdateInput): Promise<City> => {
  try {
    const { data, error } = await supabase
      .from('cities')
      .update({
        ...(input.name !== undefined && { name: input.name }),
        ...(input.code !== undefined && { code: input.code }),
        ...(input.organization_id !== undefined && { organization_id: input.organization_id }),
      })
      .eq('id', id)
      .select();
    
    if (error) {
      console.error('Error updating city:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      throw new Error('Failed to update city: City not found');
    }
    
    return data[0] as City;
  } catch (error) {
    console.error('Unexpected error in updateCity:', error);
    throw error;
  }
};

// Delete a city
export const deleteCity = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('cities')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Error deleting city:', error);
      throw error;
    }
  } catch (error) {
    console.error('Unexpected error in deleteCity:', error);
    throw error;
  }
};
